import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageTitle, useShop } from "@/context/ShopContext";
import Reveal from "@/components/Reveal";

const FABRIC_NOTES = {
  "Premium Georgette": "Lightweight and softly textured with an elegant fluid drape.",
  "Malaysian Heavy Chiffon": "Flowing chiffon with a graceful, substantial fall.",
  "Embroidered Organza": "Crisp organza with delicate embroidered detail.",
  "Plain Organza": "Lightweight, crisp and softly structured.",
  "Premium Satin": "Smooth, luminous sheen for refined occasions.",
  "Premium Jersey": "Soft stretch that stays perfectly in place all day.",
  "Pleated Chiffon": "Flowing chiffon with a beautiful pleated texture.",
  "Malaysian Crinkled Cotton": "Natural matte cotton with a signature crinkle.",
  "Shimmer Chiffon": "Weightless chiffon with a subtle luminous shimmer.",
};

export default function Collections() {
  usePageTitle("Colour Collections");
  const { meta, products } = useShop();
  const comingSoon = (meta.categories || []).filter((c) => c.status === "coming_soon");

  return (
    <div data-testid="collections-page">
      <section className="relative h-[42vh] min-h-[320px] overflow-hidden">
        <img src="/images/collections.jpg" alt="LAMSAH hijabs hanging across the nine colour families" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-espresso/40" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 flex items-end pb-14">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ivory/80 mb-4">Colour Collections</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-ivory">Every Shade. Your Style.</h1>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 md:py-28" data-testid="collections-grid">
        <Reveal>
          <p className="overline-label mb-4">The LAMSAH Collections</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">{(meta.collections || []).length || 21} Collections. {(meta.collections || []).reduce((a, c) => a + (c.count || 0), 0) || 256} Shades.</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {(meta.collections || []).map((c, i) => (
            <Reveal key={c.slug} delay={Math.min(i * 0.05, 0.3)}>
              <Link
                to={`/collections/${c.slug}`}
                data-testid={`collection-card-${c.slug}`}
                className="group block bg-white border border-beige p-8 transition-colors duration-500 hover:border-taupe h-full"
              >
                <div className="flex flex-wrap gap-1.5">
                  {c.shades.slice(0, 12).map((s, j) => (
                    <span key={s.name + j} className="w-7 h-7 rounded-full border border-beige" style={{ backgroundColor: s.hex }} title={`${s.name} — ${s.desc}`} />
                  ))}
                </div>
                <h3 className="font-serif text-2xl mt-6 group-hover:text-rose transition-colors duration-300">{c.name}</h3>
                {c.preorder && (
                  <span className="inline-block bg-espresso text-ivory text-[9px] uppercase tracking-[0.22em] px-2.5 py-1 mt-2" data-testid={`preorder-badge-${c.slug}`}>
                    Available on Pre-Order
                  </span>
                )}
                <p className="text-xs uppercase tracking-[0.15em] text-taupe mt-2">{c.fabric} · {c.count} shades</p>
                <p className="text-sm text-espresso/65 leading-relaxed mt-4 line-clamp-2">{c.story}</p>
                <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-espresso mt-6">
                  Explore {c.name}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20 md:pb-28">
        <div className="border-t border-beige pt-16 md:pt-20">
        <Reveal>
          <p className="overline-label mb-4">Shop by Colour</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">Nine Colour Families</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {(meta.families || []).map((f, i) => {
            const count = products.filter((p) => p.colour_family === f.name).length;
            return (
              <Reveal key={f.name} delay={Math.min(i * 0.05, 0.3)}>
                <Link
                  to={`/colours/${f.slug}`}
                  data-testid={`family-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group block bg-white border border-beige p-8 transition-colors duration-500 hover:border-taupe h-full"
                >
                  <div className="flex flex-wrap gap-1.5">
                    {f.shades.slice(0, 12).map((s, k) => (
                      <span key={`${s.collection}-${s.name}-${k}`} className="w-7 h-7 rounded-full border border-beige" style={{ backgroundColor: s.hex }} title={s.name} />
                    ))}
                  </div>
                  <h3 className="font-serif text-2xl mt-6 group-hover:text-rose transition-colors duration-300">{f.name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em] text-taupe mt-2">{f.shades.length} shades</p>
                  <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-espresso mt-6">
                    Shop {count} {count === 1 ? "piece" : "pieces"}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  </p>
                </Link>
              </Reveal>
            );
          })}
        </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20 md:pb-28" data-testid="fabric-collections">
        <div className="border-t border-beige pt-16 md:pt-20">
          <Reveal>
            <p className="overline-label mb-4">Shop by Fabric</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">The Fabric Library</h2>
            <p className="text-sm sm:text-base text-espresso/70 mt-4 max-w-xl">
              Every LAMSAH hijab carries two signatures — its shade and its fabric. Browse the catalogue by touch.
            </p>
            <Link
              to="/fabric-guide"
              data-testid="collections-fabric-guide-link"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-rose border-b border-rose/40 pb-0.5 mt-5 hover:text-espresso hover:border-espresso/30 transition-colors"
            >
              Read the Fabric Guide
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {(meta.fabrics || []).map((f, i) => {
              const count = products.filter((p) => p.fabric === f).length;
              return (
                <Reveal key={f} delay={Math.min(i * 0.05, 0.3)}>
                  <Link
                    to={`/shop?fabric=${encodeURIComponent(f)}`}
                    data-testid={`fabric-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    className="group block bg-white border border-beige p-8 transition-colors duration-500 hover:border-taupe h-full"
                  >
                    <h3 className="font-serif text-2xl group-hover:text-rose transition-colors duration-300">{f}</h3>
                    <p className="text-sm text-espresso/65 leading-relaxed mt-3">{FABRIC_NOTES[f] || ""}</p>
                    <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-espresso mt-6">
                      {count > 0 ? `Shop ${count} ${count === 1 ? "piece" : "pieces"}` : "Being curated"}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                    </p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28" data-testid="upcoming-collections">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="overline-label mb-4">Upcoming Collections</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">Soon at LAMSAH</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {comingSoon.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <Link
                  to={`/shop?category=${c.id}`}
                  data-testid={`upcoming-${c.id}`}
                  className="group block border border-beige bg-ivory text-center transition-colors duration-500 hover:border-taupe h-full overflow-hidden"
                >
                  {c.image && (
                    <div className="overflow-hidden bg-beige aspect-[4/3]">
                      <img src={c.image} alt={`LAMSAH ${c.label} — coming soon`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-8">
                    <p className="font-arabic text-3xl text-rose" lang="ar" dir="rtl">{c.arabic}</p>
                    <h3 className="font-serif text-2xl mt-3 tracking-wide">{c.label.toUpperCase()}</h3>
                    <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-rose border border-rose/40 px-3 py-1 mt-4">
                      Coming Soon
                    </span>
                    <p className="text-sm text-espresso/65 leading-relaxed mt-5">{c.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
